import { useCallback, useEffect, useRef, useState } from "react"
import type { KeyboardEvent as ReactKeyboardEvent } from "react"

interface UseDrawerResizeOptions {
  width: number
  onWidthChange: (width: number) => void
  enabled?: boolean
  minWidth: number
  maxWidth: number
  step?: number
  bigStep?: number
}

interface UseDrawerResizeResult {
  isResizing: boolean
  onResizeStart: (clientX: number) => void
  onResizeKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void
}

/**
 * Handle drawer width resizing via mouse/touch drag and keyboard arrows.
 */
export const useDrawerResize = ({
  width,
  onWidthChange,
  enabled = true,
  minWidth,
  maxWidth,
  step = 10,
  bigStep = 40,
}: UseDrawerResizeOptions): UseDrawerResizeResult => {
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartX = useRef<number>(0)
  const resizeStartWidth = useRef<number>(0)

  const clamp = useCallback(
    (value: number) => Math.max(minWidth, Math.min(maxWidth, value)),
    [minWidth, maxWidth]
  )

  const handleResizeStart = useCallback(
    (clientX: number) => {
      if (!enabled) return
      setIsResizing(true)
      resizeStartX.current = clientX
      resizeStartWidth.current = width
    },
    [enabled, width]
  )

  const handleResizeMove = useCallback(
    (clientX: number) => {
      if (!isResizing) return
      const deltaX = resizeStartX.current - clientX
      const desiredWidth = resizeStartWidth.current + deltaX
      const clampedWidth = clamp(desiredWidth)
      if (clampedWidth !== width) {
        onWidthChange(clampedWidth)
      }
    },
    [clamp, isResizing, onWidthChange, width]
  )

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false)
  }, [])

  const handleResizeKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!enabled) return
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return

      const amount = event.shiftKey ? bigStep : step
      const direction = event.key === "ArrowLeft" ? 1 : -1
      const desiredWidth = width + direction * amount
      const clampedWidth = clamp(desiredWidth)

      if (clampedWidth !== width) {
        event.preventDefault()
        onWidthChange(clampedWidth)
      }
    },
    [enabled, width, clamp, onWidthChange, step, bigStep]
  )

  useEffect(() => {
    if (!isResizing) return

    const onMouseMove = (e: MouseEvent) => handleResizeMove(e.clientX)
    const onTouchMove = (e: TouchEvent) =>
      handleResizeMove(e.touches[0].clientX)

    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("touchmove", onTouchMove)
    document.addEventListener("mouseup", handleResizeEnd)
    document.addEventListener("touchend", handleResizeEnd)

    return () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("touchmove", onTouchMove)
      document.removeEventListener("mouseup", handleResizeEnd)
      document.removeEventListener("touchend", handleResizeEnd)
    }
  }, [isResizing, handleResizeMove, handleResizeEnd])

  return {
    isResizing,
    onResizeStart: handleResizeStart,
    onResizeKeyDown: handleResizeKeyDown,
  }
}

export default useDrawerResize
